import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateOrganization,
  useListUsers,
  getAdminListOrganizationsQueryKey,
} from '../../api/generated/generated';
import type { AdminOrgCreate, AdminUserSummary } from '../../api/generated';
import { isApiError, getErrorMessage } from '../../api/client';
import { useSnackbar } from '../../components/common/SnackbarProvider';

interface CreateOrgDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Read a backend error's machine-readable code + details (set by AppException). */
function readApiError(err: unknown): { code?: string; orgName?: string } {
  if (!isApiError(err)) return {};
  const error = err.response?.data?.error;
  const details = error?.details?.[0] as { current_org_name?: string } | undefined;
  return { code: error?.code, orgName: details?.current_org_name };
}

export function CreateOrgDialog({ open, onClose }: CreateOrgDialogProps) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const createOrg = useAdminCreateOrganization();

  const [name, setName] = useState('');
  // An existing user (object) is assigned directly; a free-typed email (string) is invited as owner.
  const [owner, setOwner] = useState<AdminUserSummary | string | null>(null);
  const [ownerQuery, setOwnerQuery] = useState('');
  const [error, setError] = useState('');
  // When the owner is already in an org, the backend returns the current org name so we can confirm.
  const [confirm, setConfirm] = useState<{ orgName: string } | null>(null);

  const { data: usersResp, isFetching } = useListUsers(
    { email: ownerQuery || undefined, limit: 10 },
    {
      query: {
        enabled: open && ownerQuery.length > 0,
        select: (r) => (r.status === 200 ? r.data.users : []),
      },
    },
  );
  const options = usersResp ?? [];

  const reset = () => {
    setName('');
    setOwner(null);
    setOwnerQuery('');
    setError('');
    setConfirm(null);
  };

  const handleClose = () => {
    if (createOrg.isPending) return;
    reset();
    onClose();
  };

  const submit = async (data: AdminOrgCreate) => {
    setError('');
    try {
      await createOrg.mutateAsync({ data });
      queryClient.invalidateQueries({ queryKey: getAdminListOrganizationsQueryKey() });
      showSnackbar(
        willInvite
          ? `Organization created — ${ownerEmail} invited as owner.`
          : 'Organization created.',
      );
      reset();
      onClose();
    } catch (err) {
      const { code, orgName } = readApiError(err);
      if (code === 'OWNER_ALREADY_ASSIGNED') {
        setConfirm({ orgName: orgName ?? 'another organization' });
      } else if (code === 'WOULD_ORPHAN_ORG') {
        setError(
          `${ownerEmail} is the only owner of ${orgName ?? 'their organization'}. Assign a ` +
            `different owner there first, or choose another user.`,
        );
      } else {
        setError(getErrorMessage(err));
      }
    }
  };

  // Owner can be an existing user (assign by id) or a free-typed email (invite by email).
  const ownerIsExisting = typeof owner === 'object' && owner !== null;
  const ownerEmail = typeof owner === 'string' ? owner.trim() : (owner?.email ?? '');
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail);
  const ownerValid = ownerIsExisting || emailValid;
  const willInvite = !ownerIsExisting && emailValid;

  const ownerData = (): Pick<AdminOrgCreate, 'owner_user_id' | 'owner_email'> =>
    ownerIsExisting
      ? { owner_user_id: (owner as AdminUserSummary).id }
      : { owner_email: ownerEmail };

  const handleCreate = () => {
    if (!ownerValid) return;
    submit({ name: name.trim(), ...ownerData() });
  };

  const handleConfirmReassign = () => {
    setConfirm(null);
    submit({ name: name.trim(), ...ownerData(), reassign_owner: true });
  };

  const nameValid = name.trim().length >= 3;

  return (
    <>
      <Dialog open={open && !confirm} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Organization Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 3 }}
            slotProps={{
              htmlInput: { minLength: 3, maxLength: 100 }
            }}
          />
          <Autocomplete
            freeSolo
            options={options}
            value={owner}
            onChange={(_, v) => setOwner(v)}
            onInputChange={(_, v) => setOwnerQuery(v)}
            getOptionLabel={(o) =>
              typeof o === 'string'
                ? o
                : `${o.first_name} ${o.last_name}`.trim()
                  ? `${o.first_name} ${o.last_name} (${o.email})`
                  : o.email
            }
            isOptionEqualToValue={(o, v) =>
              typeof o === 'string' || typeof v === 'string' ? o === v : o.id === v.id
            }
            loading={isFetching}
            filterOptions={(x) => x}
            noOptionsText={ownerQuery ? 'No matching user — a new email will be invited' : 'Type to search users'}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={typeof o === 'string' ? o : o.id}>
                <Box>
                  <Typography variant="body2">{typeof o === 'string' ? o : o.email}</Typography>
                  {typeof o !== 'string' && o.org_id && (
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      already in an organization — will be moved
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Owner" placeholder="Existing user, or a new email to invite" />
            )}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 1,
              display: 'block'
            }}>
            {willInvite
              ? `${ownerEmail} will be invited as owner — they'll join when they sign up.`
              : 'The owner gets this organization; your own access is unchanged.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={createOrg.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!nameValid || !ownerValid || createOrg.isPending}
          >
            {createOrg.isPending ? 'Creating…' : willInvite ? 'Create & invite' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs">
        <DialogTitle>Move this user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {ownerEmail} is currently in <strong>{confirm?.orgName}</strong>. Creating
            “{name.trim()}” will move them out of it and make them the owner of the new
            organization.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={createOrg.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmReassign} disabled={createOrg.isPending}>
            {createOrg.isPending ? 'Moving…' : 'Move & create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
